/** One entry of `/settings/value-history` (global control rate, 0–1). */
export interface ControlRateChange {
  oldValue: number | null;
  newValue: number | null;
  changedBy?: string;
  date: string;
}

export interface ControlRateRange {
  /** Control rate in effect at the start of the range. */
  start: number;
  /** Control rate in effect at the end of the range. */
  end: number;
  /** Changes that happened inside the range, oldest first. */
  changes: ControlRateChange[];
}

/** Stored dates are UTC instants with an @IANA-timezone suffix. */
function parseRangeDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.split('@')[0]);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Resolves which control rate applied over the selected range.
 * Before the first change the oldest `oldValue` applies, afterwards the latest
 * `newValue`; without history (or a parsable range) the current rate is used.
 */
export function resolveControlRate(
  history: ControlRateChange[] | null | undefined,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  current: number,
): ControlRateRange {
  const start = parseRangeDate(startDate);
  const end = parseRangeDate(endDate);
  const sorted = (history || [])
    .filter((change) => !Number.isNaN(new Date(change.date).getTime()))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (!start || !end || sorted.length === 0) {
    return { start: current, end: current, changes: [] };
  }

  const rateAt = (time: Date): number => {
    let rate: number | null = sorted[0].oldValue;
    for (const change of sorted) {
      if (new Date(change.date) > time) break;
      rate = change.newValue;
    }
    return rate ?? current;
  };

  return {
    start: rateAt(start),
    end: rateAt(end),
    changes: sorted.filter((change) => {
      const date = new Date(change.date);
      return date > start && date <= end;
    }),
  };
}
